import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Settings, Store, Printer, CreditCard, Package,
  ToggleLeft, ToggleRight, Save, RefreshCw, DollarSign, Receipt
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const POSSettingsPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [settings, setSettings] = useState({
    default_customer_name: 'Walk-in Customer',
    allow_price_edit: true,
    receipt_size: '80mm',
    default_payment_mode: 'CASH',
    auto_print: false,
    show_tax_breakdown: true,
    enable_quick_item_create: true,
    enable_negative_stock: false,
    require_drawer_session: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) fetchSettings();
  }, [bid]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pos/settings');
      setSettings(s => ({ ...s, ...data }));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/pos/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving settings'));
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ value, onChange, label, description }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
      <div>
        <p className="font-medium text-slate-800">{label}</p>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-12 h-6 rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-slate-300'}`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-7' : 'left-1'}`} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/business/${bid}/pos`)} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">POS Settings</h1>
            <p className="text-sm text-slate-500">Configure your point of sale</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      {/* General Settings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Store className="w-5 h-5 text-blue-500" /> General
        </h2>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="text-sm font-medium text-slate-700">Default Customer Name</label>
          <input
            type="text"
            value={settings.default_customer_name}
            onChange={(e) => setSettings(s => ({ ...s, default_customer_name: e.target.value }))}
            className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
            placeholder="Walk-in Customer"
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="text-sm font-medium text-slate-700">Default Payment Mode</label>
          <select
            value={settings.default_payment_mode}
            onChange={(e) => setSettings(s => ({ ...s, default_payment_mode: e.target.value }))}
            className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
            <option value="BANK">Bank Transfer</option>
          </select>
        </div>
      </div>

      {/* Billing Settings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-500" /> Billing
        </h2>
        
        <Toggle
          label="Allow Price Edit"
          description="Allow editing item prices during billing"
          value={settings.allow_price_edit}
          onChange={(v) => setSettings(s => ({ ...s, allow_price_edit: v }))}
        />
        
        <Toggle
          label="Show Tax Breakdown"
          description="Display itemized tax on receipts"
          value={settings.show_tax_breakdown}
          onChange={(v) => setSettings(s => ({ ...s, show_tax_breakdown: v }))}
        />
        
        <Toggle
          label="Quick Item Create"
          description="Allow creating new items directly from POS"
          value={settings.enable_quick_item_create}
          onChange={(v) => setSettings(s => ({ ...s, enable_quick_item_create: v }))}
        />
      </div>

      {/* Inventory Settings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Package className="w-5 h-5 text-amber-500" /> Inventory
        </h2>
        
        <Toggle
          label="Allow Negative Stock"
          description="Allow sales even when stock is insufficient"
          value={settings.enable_negative_stock}
          onChange={(v) => setSettings(s => ({ ...s, enable_negative_stock: v }))}
        />

        <Toggle
          label="Require Cash Drawer"
          description="Require an open cash drawer before accepting cash payments"
          value={settings.require_drawer_session}
          onChange={(v) => setSettings(s => ({ ...s, require_drawer_session: v }))}
        />
      </div>

      {/* Print Settings */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Printer className="w-5 h-5 text-purple-500" /> Printing
        </h2>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="text-sm font-medium text-slate-700">Receipt Size</label>
          <select
            value={settings.receipt_size}
            onChange={(e) => setSettings(s => ({ ...s, receipt_size: e.target.value }))}
            className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="58mm">58mm (Small)</option>
            <option value="80mm">80mm (Standard)</option>
            <option value="A4">A4 (Full Page)</option>
          </select>
        </div>
        
        <Toggle
          label="Auto Print"
          description="Automatically print receipt after each sale"
          value={settings.auto_print}
          onChange={(v) => setSettings(s => ({ ...s, auto_print: v }))}
        />
      </div>

      {/* Keyboard Shortcuts Info */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3">Keyboard Shortcuts</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Save & Print</span><kbd className="px-2 py-1 bg-white rounded border text-xs">F6</kbd></div>
          <div className="flex justify-between"><span className="text-slate-600">Save Bill</span><kbd className="px-2 py-1 bg-white rounded border text-xs">F7</kbd></div>
          <div className="flex justify-between"><span className="text-slate-600">Hold Bill</span><kbd className="px-2 py-1 bg-white rounded border text-xs">Ctrl+B</kbd></div>
          <div className="flex justify-between"><span className="text-slate-600">Quick Item</span><kbd className="px-2 py-1 bg-white rounded border text-xs">Ctrl+I</kbd></div>
          <div className="flex justify-between"><span className="text-slate-600">Focus Search</span><kbd className="px-2 py-1 bg-white rounded border text-xs">/</kbd></div>
          <div className="flex justify-between"><span className="text-slate-600">Close Popup</span><kbd className="px-2 py-1 bg-white rounded border text-xs">Esc</kbd></div>
        </div>
      </div>
    </div>
  );
};

export default POSSettingsPage;
